import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Inbox, Search, RefreshCw, Loader2, Plus,
  Paperclip, ChevronRight, ChevronDown, Briefcase,
  Send, FolderOpen, Archive, AlertTriangle
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { formatEmailTime } from '../utils/inboxUtils';

// Category translation map (Norwegian -> English)
const categoryTranslations: Record<string, string> = {
  // Norwegian to English
  'Arbeidsrett': 'Employment Law',
  'Selskapsrett': 'Corporate Law',
  'Familie': 'Family Law',
  'Familierett': 'Family Law',
  'Eiendom': 'Real Estate',
  'Eiendomsrett': 'Real Estate',
  'Kontrakt': 'Contract Law',
  'Kontraktsrett': 'Contract Law',
  'Straff': 'Criminal Law',
  'Strafferett': 'Criminal Law',
  'Forvaltning': 'Administrative Law',
  'Forvaltningsrett': 'Administrative Law',
  'Immaterialrett': 'IP Law',
  'Inkasso': 'Debt Collection',
  'Personskade': 'Personal Injury',
  'Sjørett': 'Maritime Law',
  'Personvern': 'Data Protection',
  'M&A': 'M&A',
  'Verdipapir': 'Securities Law',
  'Entreprise': 'Construction Law',
  'Annet': 'Other',
  // Keep English as-is
  'Employment Law': 'Employment Law',
  'Corporate Law': 'Corporate Law',
  'Family Law': 'Family Law',
  'Real Estate': 'Real Estate',
  'Contract Law': 'Contract Law',
  'Criminal Law': 'Criminal Law',
  'Administrative Law': 'Administrative Law',
  'IP Law': 'IP Law',
  'IP Litigation': 'IP Litigation',
  'Debt Collection': 'Debt Collection',
  'Personal Injury': 'Personal Injury',
  'Maritime Law': 'Maritime Law',
  'Data Protection': 'Data Protection',
  'Securities Law': 'Securities Law',
  'Construction Law': 'Construction Law',
  'Other': 'Other'
};

// Helper to translate category
const translateCategory = (category: string): string => {
  return categoryTranslations[category] || category;
};

/**
 * Email List Panel Component - Minimalist Design
 * 
 * Design philosophy:
 * - Clean white background matching dashboard
 * - Subtle borders, no heavy shadows
 * - Black/gray text hierarchy
 * - Minimal visual noise
 */
const EmailListPanel = ({
  threads,
  emails,
  selectedEmail,
  selectedThread,
  expandedThread,
  filter,
  setFilter,
  searchQuery,
  setSearchQuery,
  syncing,
  onSync,
  onNewEmail,
  onSelectEmail,
  onSelectThread,
  onToggleExpand,
  panelWidth,
  isResizing,
  onResizeStart,
  outlookConnected,
  stats,
  triageView = 'triage' as string,
  onTriageViewChange,
  inboxTab = 'all' as string,  // 'focused', 'other', or 'all' - Outlook classification
  onInboxTabChange
}: { 
  threads: any[]; 
  emails: any[]; 
  selectedEmail: any; 
  selectedThread: any; 
  expandedThread: any; 
  filter: any; 
  setFilter: any; 
  searchQuery: any; 
  setSearchQuery: any; 
  syncing: any; 
  onSync: any; 
  onNewEmail: any; 
  onSelectEmail: any;
  onSelectThread: any;
  onToggleExpand: any;
  panelWidth: any;
  isResizing: any;
  onResizeStart: any;
  outlookConnected: any;
  stats: any;
  triageView?: string;
  onTriageViewChange: any;
  inboxTab?: string;
  onInboxTabChange: any;
}) => {
  const { t } = useTranslation(['inbox', 'common']);
  const { total, unread, unassigned } = stats;
  
  // Count focused and other emails (only relevant in triage view)
  const focusedCount = threads.filter(t => {
    const email = t.latest_message || t;
    return (email.inference_classification || 'focused') === 'focused';
  }).length;
  
  const otherCount = threads.filter(t => {
    const email = t.latest_message || t;
    return email.inference_classification === 'other';
  }).length;
  
  // Filter threads by triage view and inbox tab
  const filteredByInboxTab = threads.filter(t => {
    const email = t.latest_message || t;
    
    // Filter by triage view first
    if (triageView === 'triage') {
      // "New" view: Only show emails NOT assigned to a case AND needing triage
      const hasNoCase = !email.case_id;
      const needsTriage = !email.triage_status || email.triage_status === 'PENDING';
      if (!hasNoCase || !needsTriage) return false;
      
      // Then filter by inbox tab (Important/Other)
      const classification = email.inference_classification || 'focused';
      if (inboxTab === 'all') return true;
      return classification === inboxTab;
    }
    
    if (triageView === 'assigned') {
      // "Cases" view: Only show emails assigned to a case
      return !!email.case_id;
    }
    
    // "All" view: Show everything
    return true;
  });
  
  // Count flagged emails for badge
  const flaggedCount = emails.filter(e => e?.potential_new_case?.flagged).length;
  
  return (
    <div 
      className="flex flex-col bg-white h-full"
    >
      {/* Header - Clean Minimalist */}
      <div className="flex-shrink-0 px-5 py-5">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-gray-900">{t('inbox:title')}</h2>
          <div className="flex items-center gap-2">
            {outlookConnected && (
              <button
                onClick={onSync}
                disabled={syncing}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
              >
                {syncing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
              </button>
            )}
            <button
              onClick={onNewEmail}
              className="flex items-center justify-center w-8 h-8 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        {/* PRIMARY TABS - Minimal pill style */}
        {onTriageViewChange && (
          <div className="flex items-center gap-1 p-1 bg-gray-100/60 rounded-xl mb-4">
            <button
              onClick={() => onTriageViewChange('triage')}
              className={cn(
                "flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-all",
                triageView === 'triage' 
                  ? "bg-white text-gray-900 shadow-sm" 
                  : "text-gray-500 hover:text-gray-700"
              )}
            >
              {t('inbox:new')}
              {unassigned > 0 && (
                <span className="ml-1.5 text-xs text-gray-400">{unassigned}</span>
              )}
            </button>
            <button
              onClick={() => onTriageViewChange('assigned')}
              className={cn(
                "flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-1.5",
                triageView === 'assigned' 
                  ? "bg-white text-gray-900 shadow-sm" 
                  : "text-gray-500 hover:text-gray-700"
              )}
            >
              <FolderOpen className="w-3.5 h-3.5" />
              {t('inbox:cases')}
            </button>
            <button
              onClick={() => onTriageViewChange('all')}
              className={cn(
                "flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-1.5",
                triageView === 'all' 
                  ? "bg-white text-gray-900 shadow-sm" 
                  : "text-gray-500 hover:text-gray-700"
              )}
            >
              <Archive className="w-3.5 h-3.5" />
              {t('inbox:all')}
            </button>
          </div>
        )}
        
        {/* SECONDARY FILTERS - Only shown in Triage mode */}
        {triageView === 'triage' && onInboxTabChange && (
          <div className="flex items-center gap-2 mb-4">
            <div className="flex items-center gap-1.5 flex-1">
              <button
                onClick={() => onInboxTabChange('focused')}
                className={cn(
                  "flex-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-all",
                  inboxTab === 'focused'
                    ? "bg-gray-900 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
                )}
              >
                {t('inbox:important')}
                {focusedCount > 0 && <span className="ml-1 opacity-70">({focusedCount})</span>}
              </button>
              <button
                onClick={() => onInboxTabChange('other')}
                className={cn(
                  "flex-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-all",
                  inboxTab === 'other'
                    ? "bg-gray-900 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
                )}
              >
                {t('inbox:other')}
                {otherCount > 0 && <span className="ml-1 opacity-70">({otherCount})</span>}
              </button>
            </div>
            
            <button
              onClick={() => setFilter(filter === 'unread' ? 'all' : 'unread')}
              className={cn(
                "px-3 py-1.5 text-xs font-medium rounded-lg transition-all whitespace-nowrap",
                filter === 'unread'
                  ? "bg-gray-900 text-white"
                  : "bg-white text-gray-500 hover:bg-gray-50 border border-gray-200"
              )}
            >
              {t('inbox:unread')} {unread > 0 && `(${unread})`}
            </button>
          </div>
        )}
        
        {/* SECONDARY FILTERS - Shown in "Alle meldinger" view */}
        {triageView === 'all' && (
          <div className="flex items-center gap-2 mb-4">
            <button
              onClick={() => setFilter(filter === 'unread' ? 'all' : 'unread')}
              className={cn(
                "px-3 py-1.5 text-xs font-medium rounded-lg transition-all",
                filter === 'unread'
                  ? "bg-gray-900 text-white"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              )}
            >
              {t('inbox:unread')} {unread > 0 && `(${unread})`}
            </button>
            <button
              onClick={() => setFilter(filter === 'flagged' ? 'all' : 'flagged')}
              className={cn(
                "px-3 py-1.5 text-xs font-medium rounded-lg transition-all flex items-center gap-1",
                filter === 'flagged'
                  ? "bg-gray-900 text-white"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              )}
            >
              <AlertTriangle className="w-3 h-3" />
              {t('inbox:flagged')} {flaggedCount > 0 && `(${flaggedCount})`}
            </button>
          </div>
        )}
        
        {/* Search - Clean minimal style */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('inbox:search.placeholder')}
            className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl bg-gray-50 border-0 focus:outline-none focus:ring-2 focus:ring-gray-200 placeholder:text-gray-400"
          />
        </div>
      </div>
      
      {/* Email List */}
      <div className="flex-1 overflow-y-auto">
        {filteredByInboxTab.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 p-6">
            <Inbox className="w-10 h-10 mb-3 text-gray-200" />
            <p className="text-sm text-gray-500">
              {inboxTab === 'other' ? t('inbox:empty.noEmailsInOther') : t('inbox:empty.noEmails')}
            </p>
          </div>
        ) : (
          filteredByInboxTab.map((thread) => {
            const email = thread.latest_message || thread;
            const isSelected = selectedEmail?.id === email?.id;
            const isExpanded = expandedThread === thread.conversation_id;
            const hasMultiple = thread.message_count > 1;
            const isUnread = !email?.read_by?.length && !email?.is_read;
            const analysis = email?.ai_analysis;
            
            return (
              <div key={thread.id || email?.id}>
                {/* Main row - Clean Minimalist Design */}
                <div
                  onClick={() => {
                    onSelectEmail(email);
                    onSelectThread(thread);
                  }}
                  className={cn(
                    "px-5 py-4 cursor-pointer transition-all border-b border-gray-100",
                    isSelected 
                      ? "bg-gray-50" 
                      : "hover:bg-gray-50/50"
                  )}
                >
                  <div className="flex items-start gap-3">
                    {/* Dot for unread - Minimal style */}
                    <div className="flex-shrink-0 mt-2">
                      {isUnread ? (
                        <div className="w-1.5 h-1.5 rounded-full bg-gray-900" />
                      ) : (
                        <div className="w-1.5 h-1.5 rounded-full bg-transparent" />
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className={cn(
                          "text-sm truncate",
                          isUnread ? "font-semibold text-gray-900" : "font-medium text-gray-600"
                        )}>
                          {email?.direction === 'OUTBOUND' && (
                            <Send className="w-3 h-3 inline mr-1.5 text-gray-400" />
                          )}
                          {email?.direction === 'OUTBOUND' 
                            ? (email?.to?.[0]?.name || email?.to?.[0]?.email?.split('@')[0] || email?.from?.name || t('inbox:unknown'))
                            : (email?.from?.name || email?.from?.email?.split('@')[0] || t('inbox:unknown'))
                          }
                        </span>
                        <span className="text-xs text-gray-400 flex-shrink-0">
                          {formatEmailTime(email?.created_at)}
                        </span>
                      </div>
                      
                      <p className={cn(
                        "text-sm truncate mb-2",
                        isUnread ? "text-gray-700" : "text-gray-400"
                      )}>
                        {email?.subject || `(${t('inbox:email.noSubject')})`}
                      </p>
                      
                      {/* Tags row - Minimal gray style */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Category badge - Gray */}
                        {analysis?.category && (
                          <span className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-500 rounded font-medium">
                            {translateCategory(analysis.category)}
                          </span>
                        )}
                        
                        {/* Thread count */}
                        {hasMultiple && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onToggleExpand(thread.conversation_id);
                            }}
                            className="text-[10px] text-gray-400 hover:text-gray-600 flex items-center gap-0.5 px-1.5 py-0.5 rounded hover:bg-gray-100"
                          >
                            {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                            {thread.message_count}
                          </button>
                        )}
                        
                        {/* Attachment indicator */}
                        {(email?.has_attachments || thread.has_attachments) && (
                          <Paperclip className="w-3 h-3 text-gray-300" />
                        )}
                        
                        {/* Case assigned indicator */}
                        {email?.case_id && (
                          <span className="flex items-center gap-1 text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                            <Briefcase className="w-2.5 h-2.5" />
                            <span>Assigned</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Expanded thread - Minimal */}
                {hasMultiple && isExpanded && (
                  <div className="bg-gray-50/50">
                    {thread.messages?.slice(0, 10).map((msg, idx) => {
                      const isFirstMsg = idx === 0;
                      const msgIsSelected = msg.id === selectedEmail?.id;
                      const msgIsUnread = !msg?.read_by?.length && !msg?.is_read;
                      
                      return (
                        <div
                          key={msg.id || idx}
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectEmail(msg);
                          }}
                          className={cn(
                            "px-5 py-2.5 cursor-pointer transition-all border-b border-gray-100/50 last:border-b-0 ml-6",
                            msgIsSelected 
                              ? 'bg-white' 
                              : 'hover:bg-white/50'
                          )}
                        >
                          <div className="flex items-center gap-3">
                            {/* Unread indicator */}
                            <div className={cn(
                              "w-1 h-1 rounded-full flex-shrink-0",
                              msgIsUnread ? "bg-gray-900" : "bg-transparent"
                            )} />
                            
                            {/* Message info */}
                            <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                              <span className={cn(
                                "text-xs truncate",
                                msgIsUnread ? "font-semibold text-gray-900" : "text-gray-600"
                              )}>
                                {msg.direction === 'OUTBOUND' 
                                  ? 'Du' 
                                  : (msg.from?.name || msg.from?.email?.split('@')[0])}
                              </span>
                              <span className="text-[10px] text-gray-400 flex-shrink-0">
                                {formatEmailTime(msg.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {thread.messages?.length > 10 && (
                      <div className="px-5 py-2 text-[10px] text-gray-400 text-center">
                        + {thread.messages.length - 10} flere
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default EmailListPanel;
